/**
 * Poll-options resource: the current space's poll question and option list,
 * plus the editorial write.
 *
 * Both the poll a visitor votes in and the widget an editor configures it with
 * render inside the question instance, so the space key comes from the instance
 * rather than a parameter.
 *
 * `setSpacePollOptionsAsync` takes the whole poll, because the write replaces
 * it wholesale — a caller changing one part reads the rest back and sends it
 * along.
 */
import type { PollOptionsResult } from '@repo/shared/orpc/procedures/poll-options';
import { useApiQueryUtils } from 'system/api-client-context';
import { type Datastore } from 'util/datastore';
import { fireAndForget } from 'util/hooks';

import { instanceParamsOf } from './silo-params';
import { useInstanceQuery } from './use-silo-query';

export type { PollOptionsResult } from '@repo/shared/orpc/procedures/poll-options';

// One shared empty poll, so a still-loading read returns a stable reference
// rather than a fresh object every render.
const NO_POLL: PollOptionsResult = { question: null, options: [] };

/** The current space's poll. Empty while loading, and without one set. */
export function useSpacePollOptions(): { poll: PollOptionsResult; isLoading: boolean } {
    const utils = useApiQueryUtils();
    const query = useInstanceQuery({ procedure: utils.spaces.pollOptions.get });
    return { poll: query.data ?? NO_POLL, isLoading: query.isLoading };
}

/**
 * Replaces the current space's poll, patching the cached read as it goes.
 *
 * The patch is what keeps consecutive edits honest. Each editor sends a whole
 * poll built from the one it last read, so an edit made before the previous
 * write's refetch lands would otherwise send a stale list and undo it — two
 * quick presses of an option's reorder button being the easy way to hit that.
 *
 * What the server stored wins over what the caller asked for, since it trims
 * each option and drops the blank ones. A failed write restores what was
 * cached before it, and either way the read is invalidated on the way out so
 * the cache reconciles with the server.
 */
export async function setSpacePollOptionsAsync(datastore: Datastore, { question, options }: PollOptionsResult): Promise<void> {
    const params = instanceParamsOf(datastore);
    const queryClient = datastore.getQueryClient();
    const utils = datastore.getApiQueryUtils();
    const pollKey = utils.spaces.pollOptions.get.queryOptions({ input: params }).queryKey;

    await queryClient.cancelQueries({ queryKey: pollKey });
    const previous = queryClient.getQueryData<PollOptionsResult>(pollKey);
    queryClient.setQueryData(pollKey, { question, options });

    try {
        const stored = await datastore.getApiClient().spaces.pollOptions.set({ ...params, question, options });
        queryClient.setQueryData(pollKey, stored);
    } catch (err) {
        queryClient.setQueryData(pollKey, previous);
        throw err;
    } finally {
        fireAndForget(queryClient.invalidateQueries({ queryKey: pollKey }));
    }
}
